﻿import { useCallback, useEffect, useMemo, useState } from 'react'
import { PARTIAL_NOTE, PARTIAL_TITLE, WITHHELD, WITHHELD_TITLE, partialOf } from '../api/answer'
import type { Api } from '../api'
import { ApiError } from '../api/types'
import type {
  ApiFailure,
  Currency,
  SalesFilters,
  SalesLine,
  SalesLineFilterRow,
  SalesLineFilters,
  SalesLineSort,
  SalesLinesResponse,
  SalesPeriod,
} from '../api/types'
import { CollapsibleStats, EmptyState, Loading, SearchSelect, Segmented, compact, inputClass, money } from './ui'
import { Explain, ExplainMark } from './DevModeContext'
import { FailureState } from './FailureState'
import { ExportButtons } from './ExportButtons'
import type { ExportDoc, ExportRow } from '../export/model'
import { useSortMirror } from './gridSort'
import { GridChrome, ResizeHandle, useGridLayout } from './gridLayout'
import { quantityUnits } from './units'

const GRID_ID = 'sales-invoice-lines'

/**
 * INVOICE LINES — the posted sales ledger, one row per line.
 *
 * Management asked for this page by sending a spreadsheet rather than a
 * specification (Sales report study/data.xlsx). Its 25 columns are reproduced
 * here in their own order, so somebody holding the file recognises the screen;
 * db/platform/022_sales_lines.sql carries the column-by-column mapping and the
 * proof that the figures reconcile to their totals to the cent.
 *
 * ---------------------------------------------------------------------------
 * SALES-ONLY FILE. Sales' own component, like the receivables report's
 * TransactionsTable is its. Nothing here imports from another report.
 *
 * ---------------------------------------------------------------------------
 * WHAT THE READER MUST BE ABLE TO TRUST
 *
 * The TOTALS ARE OF THE WHOLE FILTERED SET, not the hundred lines on screen —
 * a reader comparing this with their spreadsheet is comparing totals, and a
 * total that quietly meant "this page" would be wrong in the way that matters.
 * The grid says which slice it is showing beside them.
 *
 * A masked figure is an em dash, never a zero: cost, charges and margin need
 * the sales-cost permission, and the procedure masks before it selects.
 *
 * Sorting and paging happen on the SERVER, so a sort orders every line the
 * caller may see rather than the page in front of them — and every page is one
 * audited read.
 */

/** The grid, in the spreadsheet's own column order. */
interface ColumnSpec {
  key: string
  header: string
  /** Numeric columns are right-aligned and export as numbers. */
  kind?: 'text' | 'money' | 'qty' | 'pct' | 'date'
  /** The procedure's sort name; absent means the column cannot be sorted. */
  sort?: SalesLineSort
  value: (l: SalesLine) => string | number | null
  /** Money columns vanish to a dash together when Sales_Cost is missing. */
  cost?: boolean
  /**
   * Shown in the default "Key columns" view. All 25 are always one click and
   * every extract away — but 25 columns put the money off the right-hand edge,
   * and a ledger whose figures you have to go looking for is a worse ledger
   * than one that shows them.
   */
  key_?: boolean
  /** Set from `key_` for the layout; see LAYOUT_COLUMNS below. */
  hiddenByDefault?: boolean
}

/** In the spreadsheet's own order, so somebody holding the file recognises it. */
const COLUMNS: ColumnSpec[] = [
  { key: 'company', header: 'Company Name', value: (l) => l.company },
  /* Business unit is not a key column: it is one of the four standing filters
     above the grid, so on a line it repeats what the bar already says. */
  { key: 'bu', header: 'Business Unit', value: (l) => l.businessUnit },
  { key: 'bt', header: 'Business Team', value: (l) => l.businessTeam },
  { key: 'dimRegion', header: 'Dim Region', value: (l) => l.companyRegion },
  { key: 'custRegion', header: 'Cust Region', value: (l) => l.customerRegion },
  { key: 'sp', header: 'Salesperson', sort: 'Salesperson', value: (l) => l.salesperson, key_: true },
  { key: 'itemNo', header: 'Item Number', value: (l) => l.itemNumber },
  { key: 'product', header: 'Product Name', sort: 'Product', value: (l) => l.product, key_: true },
  { key: 'supplier', header: 'Supplier Name', sort: 'Supplier', value: (l) => l.supplier, key_: true },
  { key: 'custNo', header: 'Customer Number', value: (l) => l.customerNumber },
  { key: 'customer', header: 'Customer Name', sort: 'Customer', value: (l) => l.customer, key_: true },
  { key: 'location', header: 'Location', value: (l) => l.location },
  { key: 'district', header: 'Sales District', sort: 'District', value: (l) => l.district },
  { key: 'docType', header: 'Doc Type', sort: 'DocType', value: (l) => l.docType, key_: true },
  { key: 'docNo', header: 'Doc Number', sort: 'DocNumber', value: (l) => l.docNumber, key_: true },
  { key: 'batch', header: 'Batch No.', sort: 'Batch', value: (l) => l.batch },
  { key: 'date', header: 'Invoice Date', kind: 'date', sort: 'InvoiceDate', value: (l) => l.invoiceDate, key_: true },
  { key: 'uom', header: 'Base UOM', value: (l) => l.uom },
  { key: 'qty', header: 'Qty Sold', kind: 'qty', sort: 'Qty', value: (l) => l.qty, key_: true },
  { key: 'sales', header: 'Sales', kind: 'money', sort: 'Sales', value: (l) => l.sales, key_: true },
  { key: 'cost', header: 'Cost', kind: 'money', sort: 'Cost', value: (l) => l.cost, cost: true, key_: true },
  { key: 'charges', header: 'Charges', kind: 'money', sort: 'Charges', value: (l) => l.charges, cost: true },
  { key: 'gp', header: 'Gross Margin', kind: 'money', sort: 'Gp', value: (l) => l.gp, cost: true, key_: true },
  { key: 'gpPct', header: 'Gross Margin %', kind: 'pct', sort: 'GpPct', value: (l) => l.gpPct, cost: true, key_: true },
  { key: 'kluber', header: 'Kluber Code', value: (l) => l.kluberCode },
]

/**
 * ONE COLUMN LIST, ONE CONTROL.
 *
 * This grid used to carry its own Key / All 25 toggle — itself labelled
 * "Columns" — which swapped the column ARRAY underneath the reader. Beside the
 * shared column layout that was two controls for one job, and worse: changing
 * the array changes the key set, which the layout reads as a different grid and
 * resets, so every toggle would have thrown away the reader's widths, order and
 * pins.
 *
 * So the twelve key columns are simply the default now and the other thirteen
 * start hidden. The preset below and the Columns panel write to the same state.
 */
const LAYOUT_COLUMNS: ColumnSpec[] = COLUMNS.map((c) => ({ ...c, hiddenByDefault: !c.key_ }))
const EXTRA_KEYS = COLUMNS.filter((c) => !c.key_).map((c) => c.key)

const PAGE_SIZES = [100, 250, 500]

/** A bulk extract is still audited page by page, but it is not unbounded. */
const EXPORT_ALL_CAP = 5000

const DASH = '—'

export function SalesInvoiceLines({
  api,
  viewAs,
  period,
  range,
  currency,
  filters,
  contextLines,
}: {
  api: Api
  viewAs: string | null
  period: SalesPeriod
  range: { from: string; to: string; label: string } | null
  currency: Currency
  filters: SalesFilters
  /** The report-level selection, already stated as sentences for the extract. */
  contextLines: string[]
}) {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(100)
  const [sortBy, setSortBy] = useState<SalesLineSort>('InvoiceDate')
  const [sortDesc, setSortDesc] = useState(true)
  const [search, setSearch] = useState('')
  const [applied, setApplied] = useState('')
  const [showMore, setShowMore] = useState(false)
  const [pageFilters, setPageFilters] = useState<SalesLineFilters>({ hideZeroQty: true })
  const [options, setOptions] = useState<SalesLineFilterRow[]>([])
  const [optionsFailed, setOptionsFailed] = useState(false)
  const [optionsTry, setOptionsTry] = useState(0)
  const [data, setData] = useState<SalesLinesResponse | null>(null)
  /** The new permission model answered (M8). */
  const [v2, setV2] = useState(false)
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  const [loading, setLoading] = useState(true)
  const [exportScope, setExportScope] = useState<'page' | 'all'>('page')
  const layout = useGridLayout(GRID_ID, LAYOUT_COLUMNS)
  /* THE SORT RIDES INSIDE A SAVED VIEW. An unrecognised key -- a column since
     dropped, or one this reader may not be shown -- falls back to this grid's
     own order rather than sorting by nothing. See useSortMirror. */
  useSortMirror(GRID_ID, { key: sortBy, desc: sortDesc }, (s) => {
    const col = COLUMNS.find((c) => c.sort && c.sort === s.key)
    setSortBy(col?.sort ?? 'InvoiceDate')
    setSortDesc(col ? s.desc : true)
  })
  const [dragKey, setDragKey] = useState<string | null>(null)
  const [overKey, setOverKey] = useState<string | null>(null)

  /* A MISSING LIST IS SAID, NOT SWALLOWED. This is a second read, asked once
     per identity, and when it failed the Document control and the More-filters
     menus quietly offered only "All" — which reads as "there are no such
     documents", not "the list did not arrive". The empty fallback stays, so the
     ledger keeps working; the flag puts a line under the controls instead.

     It does not go to `failure`: the ledger's own read may well have succeeded,
     and hiding a working grid over a missing dropdown is the larger harm. A
     refusal cannot slip through here either — /sales/line-filters carries the
     same guards as /sales/lines, so a refused reader is refused by the grid's
     read and gets FailureState from that. */
  useEffect(() => {
    let cancelled = false
    setOptionsFailed(false)
    api
      .salesLineFilterOptions()
      .then((r) => !cancelled && setOptions(r))
      .catch(() => {
        if (!cancelled) {
          setOptions([])
          setOptionsFailed(true)
        }
      })
    return () => {
      cancelled = true
    }
  }, [api, viewAs, optionsTry])

  /* The query the screen currently represents — one object, so the loader and
     the extract can never disagree about what is being shown. */
  const query = useMemo(
    () => ({
      ...filters,
      ...pageFilters,
      period,
      from: range?.from,
      to: range?.to,
      search: applied || undefined,
      currency,
    }),
    [filters, pageFilters, period, range, applied, currency, viewAs],
  )

  const load = useCallback(async () => {
    setLoading(true)
    setFailure(null)
    try {
      const res = await api.salesLines({ ...query, page, pageSize, sortBy, sortDesc })
      setData(res.data)
      setV2(res.vis !== null)
    } catch (e: unknown) {
      setFailure(e instanceof ApiError ? e.failure : { kind: 'error', title: 'Unexpected error.' })
    } finally {
      setLoading(false)
    }
  }, [api, query, page, pageSize, sortBy, sortDesc])

  useEffect(() => {
    void load()
  }, [load])

  // Any change to WHAT is being asked returns to page one; a filter that left
  // the reader on page 12 of a shorter result would look like an empty book.
  useEffect(() => {
    setPage(1)
  }, [query, pageSize, sortBy, sortDesc])

  const rows = data?.rows ?? []
  const totals = data?.totals
  /* UNDER THE NEW MODEL a line's figure can be withheld while the next shows, one
     piece of access at a time (M8): a blank cell is withheld, a total is the sum of
     what shows, withheld when no line shows it, and marked when it leaves lines out
     (TotalsPartial). Under today's model these draw what they drew before. */
  const totalsPartial = partialOf(totals)
  const blank = v2 && (data?.totalRows ?? 0) > 0 ? WITHHELD : DASH
  const tot = (v: number | null | undefined, key: string, fmt: (n: number) => string) =>
    v == null ? blank : `${fmt(v)}${totalsPartial.has(key) ? '*' : ''}`
  const totTitle = (v: number | null | undefined, key: string, fmt: (n: number) => string, none?: string) =>
    v == null ? (v2 ? WITHHELD_TITLE : none) : totalsPartial.has(key) ? `${fmt(v)}. ${PARTIAL_TITLE}.` : fmt(v)
  const totalRows = data?.totalRows ?? 0

  /* WHAT THE FIGURES ARE IN, 16 Sep 2026.

     Each LINE carries its own currency — one line belongs to one company, so
     that is a plain column with no ambiguity, and it is what the grid prints.

     The TOTALS strip gets a code only when every line on this page agrees,
     folded here in the browser. Asking the server for the whole filtered set's
     answer meant four MIN/MAX window aggregates on nvarchar, and the identical
     shape cost the KPI head +1,799ms; this is the paged detail read, re-run on
     every sort and page turn, so it was the worst place of all to pay it.

     That makes the strip's code a statement about THIS PAGE, and the caption
     below says so rather than letting it read as a fact about the selection. */
  const lineCode = (() => {
    const set = new Set(rows.map((r) => r.currency).filter((c): c is string => !!c))
    return set.size === 1 ? [...set][0]! : null
  })()
  /* Each LINE is one unit, so the page mixes units exactly when its lines
     disagree -- no per-line flag is needed here, unlike the Explorer's groups.
     The wording is shared with the Explorer: see components/units.ts. */
  const lineUom = quantityUnits(rows.map((r) => ({ unit: r.uom ?? null })))
  const pages = Math.max(1, Math.ceil(totalRows / pageSize))
  const costMasked = rows.length > 0 && rows.every((r) => r.gp === null)
  /* Under the new model the banner names only the columns blank on every line:
     margin needs sales too, so cost and charges may well be showing (16d's review). */
  const blankEverywhere = rows.length === 0 ? []
    : ([['cost', 'Cost'], ['charges', 'Charges'], ['gp', 'Margin']] as const)
      .filter(([k]) => rows.every((r) => r[k] === null)).map(([, label]) => label)
  const activeMore = [
    pageFilters.docType, pageFilters.district, pageFilters.location,
    pageFilters.kluber,
  ].filter(Boolean).length

  /* One source for the grid and the extract, so they can never disagree. */
  const shown = layout.columns

  /* Which preset the current hidden set corresponds to, if any. A reader who
     has ticked their own combination matches neither, and the segmented control
     then shows nothing selected rather than claiming one of them. */
  const hiddenNow = new Set(layout.hidden)
  const preset =
    hiddenNow.size === 0
      ? 'all'
      : hiddenNow.size === EXTRA_KEYS.length && EXTRA_KEYS.every((k) => hiddenNow.has(k))
        ? 'key'
        : ''

  // The new model names the chain menu 'chain' (057, part 14b); it is the same menu.
  const of = (kind: SalesLineFilterRow['kind']) =>
    options.filter((o) => o.kind === kind || (kind === 'kluber' && o.kind === 'chain'))

  const setFilter = (k: keyof SalesLineFilters, v: string | undefined) =>
    setPageFilters((f) => ({ ...f, [k]: v || undefined }))

  const toggleSort = (col: ColumnSpec) => {
    if (!col.sort) return
    if (sortBy === col.sort) setSortDesc((d) => !d)
    else {
      setSortBy(col.sort)
      setSortDesc(true)
    }
  }

  const cell = (l: SalesLine, c: ColumnSpec): string => {
    const v = c.value(l)
    if (v === null || v === undefined || v === '') {
      if (!c.kind) return ''
      // A blank date, or a margin % on a line with no price, was never a figure to withhold.
      return c.kind === 'date' || (c.kind === 'pct' && l.gp !== null && l.sales !== null) ? DASH : blank
    }
    switch (c.kind) {
      case 'money':
        // THIS LINE's own currency, not the page's: a line belongs to one
        // company, so the code beside its amount is always exactly right —
        // even on a page that mixes companies, where no single code would be.
        return money(Number(v), currency, l.currency)
      case 'qty':
        return Number(v).toLocaleString('en-US', { maximumFractionDigits: 2 })
      case 'pct':
        return `${(Number(v) * 100).toFixed(1)}%`
      case 'date':
        return dayLabel(String(v))
      default:
        return String(v)
    }
  }

  /* THE EXTRACT — the page as shown, or the whole filtered set fetched a page
     at a time (each one audited, capped, and the cap disclosed). */
  const buildDoc = async (): Promise<ExportDoc | null> => {
    if (loading || rows.length === 0) return null
    let lines = rows
    let truncated = false
    if (exportScope === 'all' && totalRows > rows.length) {
      const want = Math.min(totalRows, EXPORT_ALL_CAP)
      const all: SalesLine[] = []
      try {
        for (let p = 1; all.length < want; p++) {
          const chunk = (await api.salesLines({ ...query, page: p, pageSize: 500, sortBy, sortDesc })).data
          if (chunk.rows.length === 0) break
          all.push(...chunk.rows)
          if (p > 40) break
        }
      } catch (e: unknown) {
        /* A REFUSAL MID-EXTRACT IS NOT AN EXPORT FAILURE. These are the only
           reads an extract makes, and a page refused here — access revoked or
           narrowed since the grid loaded — used to reach ExportButtons as
           "Export failed — try again": no reason, no reference, and advice
           that only repeats the refused request. It goes to the grid's own
           failure path instead, which is the screen a reload would show this
           reader anyway. Any other error still throws to the buttons. */
        if (e instanceof ApiError && (e.failure.kind === 'no-permissions' || e.failure.kind === 'forbidden')) {
          setFailure(e.failure)
        }
        throw e
      }
      lines = all.slice(0, want)
      truncated = totalRows > lines.length
    }

    /* An extract carries the columns on screen — the platform's rule. Said
       here rather than left to be noticed, because the reader who wanted the
       spreadsheet wanted all 25 of them. */
    const columnNote =
      shown.length < COLUMNS.length
        ? ` Showing ${shown.length} of the ${COLUMNS.length} columns — the rest are hidden in this grid, and the extract follows what is on screen.`
        : ''
    const scopeNote =
      exportScope === 'all'
        ? truncated
          ? `The first ${lines.length.toLocaleString('en-US')} of ${totalRows.toLocaleString('en-US')} lines — an extract is capped at ${EXPORT_ALL_CAP.toLocaleString('en-US')}. Narrow the filters to reach the rest.`
          : `All ${lines.length.toLocaleString('en-US')} lines in this selection.`
        : `Page ${page} of ${pages} — ${lines.length.toLocaleString('en-US')} of ${totalRows.toLocaleString('en-US')} lines. The totals above are of the whole selection, not this page.`

    const chosen = [
      pageFilters.docType && `Document type: ${pageFilters.docType}`,
      pageFilters.district && `Sales district: ${pageFilters.district}`,
      pageFilters.location && `Location: ${pageFilters.location}`,
      // Supplier and customer region are on the STANDING bar now, so
      // salesExportContext already names them in the head of this file —
      // repeating them here would print each one twice.
      pageFilters.kluber && `Kluber code: ${pageFilters.kluber}`,
      applied && `Search: contains "${applied}"`,
    ].filter(Boolean) as string[]

    return {
      report: 'Sales',
      page: 'Invoice Lines',
      title: 'Posted sales lines',
      subtitle: 'One row per posted line, in the order and columns of the requested spreadsheet.',
      context: [
        ...contextLines,
        ...chosen,
        pageFilters.hideZeroQty === false
          ? 'Zero-quantity lines: included'
          : 'Zero-quantity lines: hidden (a line with no quantity is a correction)',
        `Sorted by ${sortBy} ${sortDesc ? 'descending' : 'ascending'}`,
      ],
      privileged: viewAs,
      currency,
      kpis: totals
        ? [
            { label: 'Lines', value: totalRows.toLocaleString('en-US'), secondary: 'in this selection' },
            { label: 'Sales', value: tot(totals.sales, 'sales', (n) => compact(n, currency, lineCode)), secondary: totals.sales === null ? undefined : money(totals.sales, currency, lineCode) },
            { label: 'Cost', value: tot(totals.cost, 'cost', (n) => compact(n, currency, lineCode)), secondary: totals.cost === null ? (v2 ? undefined : 'Requires the sales-cost permission') : undefined },
            { label: 'Charges', value: tot(totals.charges, 'charges', (n) => compact(n, currency, lineCode)) },
            { label: 'Gross margin', value: tot(totals.gp, 'gp', (n) => compact(n, currency, lineCode)), secondary: totals.gpPct === null ? undefined : `${(totals.gpPct * 100).toFixed(2)}% of sales${totalsPartial.has('gpPct') ? '*' : ''}` },
          ]
        : undefined,
      tables: [
        {
          columns: shown.map((c) => ({
            header: c.header,
            kind:
              c.kind === 'money' ? ('money' as const)
              : c.kind === 'qty' ? ('num2' as const)
              : c.kind === 'pct' ? ('pct' as const)
              : ('text' as const),
          })),
          rows: lines.map<ExportRow>((l) => ({
            cells: shown.map((c) => {
              const v = c.value(l)
              if (v === null || v === undefined) {
                return v2 && c.kind && c.kind !== 'text' && c.kind !== 'date'
                  && !(c.kind === 'pct' && l.gp !== null && l.sales !== null) ? WITHHELD : null
              }
              if (c.kind === 'date') return dayLabel(String(v))
              return c.kind && c.kind !== 'text' ? Number(v) : String(v)
            }),
          })),
          note: scopeNote + columnNote + (v2 && totalsPartial.size > 0 ? ` ${PARTIAL_NOTE}` : ''),
        },
      ],
    }
  }

  if (failure) return <FailureState failure={failure} onRetry={() => void load()} />

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* ---- the page's own controls ---- */}
      <div className="flex shrink-0 flex-wrap items-end justify-between gap-x-4 gap-y-3 border-b border-(--color-line) px-5 py-3">
        <form
          /* min-w-0 so the form can actually give the width its input offered:
             a flex item's default min-width is auto, which refuses to shrink
             below its content and would have made the flex-1 above inert. */
          className="flex min-w-0 flex-1 items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            setApplied(search.trim())
          }}
        >
          {/* min-w 180, not 270. The controls on this row measured 1,255px
              against 1,166px of content at 1280, so the row wrapped to two and
              cost 73px of height — on the one screen that had 115px left for
              its grid. The search box is the only elastic thing here; letting
              it give 90px keeps every control on one row instead of dropping
              one of them. The placeholder is what shortens, not the field. */}
          <label className="flex min-w-0 flex-1 flex-col gap-1">
            {/* SIX COLUMNS, not one -- wider than the standing Customer filter,
                which is the distinction worth being able to look up when the
                two return different counts. */}
            <Explain surface="linesFilters" measureKey="search" className="eyebrow">
              Find a line
            </Explain>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Customer, product, item, invoice or batch…"
              className={`${inputClass} min-w-[180px]`}
            />
          </label>
          <button
            type="submit"
            className="rounded-md border border-(--color-line-strong) px-3 py-2 text-(length:--text-small) font-semibold text-(--color-ink-2) hover:bg-(--color-surface-2)"
          >
            Search
          </button>
        </form>

        <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
          <div>
            <Explain surface="linesFilters" measureKey="docType" className="eyebrow mb-1.5 block">
              Document
            </Explain>
            <Segmented
              label="Document type"
              value={pageFilters.docType ?? ''}
              onChange={(v) => setFilter('docType', v)}
              options={[
                { value: '', label: 'All' },
                ...of('docType').map((o) => ({ value: o.code ?? '', label: o.label ?? '' })),
              ]}
            />
          </div>
          <div>
            <div className="eyebrow mb-1.5">Preset</div>
            {/* Two clicks that write the SAME state the Columns panel writes,
                rather than a second column control beside it. */}
            <Segmented<string>
              label="Column preset"
              value={preset}
              onChange={(v) => layout.setHidden(v === 'key' ? EXTRA_KEYS : [])}
              options={[
                { value: 'key', label: 'Key', title: 'The columns that fit without scrolling sideways' },
                { value: 'all', label: `All ${COLUMNS.length}`, title: 'Every column of the requested spreadsheet, in its order' },
              ]}
            />
          </div>
          <button
            type="button"
            onClick={() => setShowMore((s) => !s)}
            aria-expanded={showMore}
            className={`rounded-md border px-3 py-2 text-(length:--text-small) font-semibold transition-colors ${
              activeMore > 0
                ? 'border-(--color-navy-700) bg-(--color-navy-50) text-(--color-navy-700)'
                : 'border-(--color-line-strong) text-(--color-ink-2) hover:bg-(--color-surface-2)'
            }`}
          >
            More filters{activeMore > 0 ? ` · ${activeMore}` : ''}
          </button>
          <label className="flex cursor-pointer items-center gap-2 pb-2 text-(length:--text-small) text-(--color-ink-2)">
            <input
              type="checkbox"
              checked={pageFilters.hideZeroQty !== false}
              onChange={(e) => setPageFilters((f) => ({ ...f, hideZeroQty: e.target.checked }))}
              className="h-3.5 w-3.5 accent-[var(--color-navy-700)]"
            />
            {/* ON BY DEFAULT, which is the fact worth being able to look up:
                charges-only documents and adjustments carry money but no goods,
                and the ledger hides them until somebody unticks this. */}
            <Explain surface="linesFilters" measureKey="hideZeroQty">
              Hide zero-quantity lines
            </Explain>
          </label>
          <div className="flex items-end gap-2 pb-0.5">
            {totalRows > rows.length && (
              <Segmented
                label="Extract scope"
                value={exportScope}
                onChange={setExportScope}
                options={[
                  { value: 'page', label: 'This page', title: 'Extract the lines on screen' },
                  { value: 'all', label: `All ${compactCount(Math.min(totalRows, EXPORT_ALL_CAP))}`, title: `Extract every line in this selection, up to ${EXPORT_ALL_CAP.toLocaleString('en-US')}` },
                ]}
              />
            )}
            <ExportButtons build={buildDoc} />
            <GridChrome api={layout} />
          </div>
        </div>
      </div>

      {/* The option lists did not arrive (see the effect that reads them). The
          same band as the cost-mask notice below, because it is the same kind
          of statement: this part of the screen is short, and here is why. */}
      {optionsFailed && (
        <p className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 border-b border-(--color-line) bg-(--color-surface-2) px-5 py-2 text-(length:--text-small) text-(--color-ink-3)">
          The Document, Sales District, Location and Kluber Code lists could not be loaded, so they offer only All.
          <button
            type="button"
            onClick={() => setOptionsTry((n) => n + 1)}
            className="font-semibold text-(--color-navy-700) hover:underline"
          >
            Try again
          </button>
        </p>
      )}

      {/* ---- the page's secondary filters, folded away until wanted ---- */}
      {showMore && (
        <div className="flex shrink-0 flex-wrap items-end gap-3 border-b border-(--color-line) bg-(--color-surface-2) px-5 py-3">
          {(
            [
              /* Supplier and Customer Region used to be here. They moved up
                 to the standing bar on 10 Sep 2026, where one control
                 narrows all six tabs instead of only this one. What is left
                 are the dimensions that exist on a LINE and nowhere else. */
              ['Sales District', 'district', 'district'],
              ['Location', 'location', 'location'],
              ['Kluber Code', 'kluber', 'kluber'],
            ] as const
          ).map(([label, kind, field]) => (
            <div key={field} className="flex min-w-[150px] flex-1 flex-col gap-1 max-w-[210px]">
              <Explain surface="linesFilters" measureKey={field} className="eyebrow">
                {label}
              </Explain>
              <SearchSelect
                label={label}
                value={(pageFilters[field] as string | undefined) ?? ''}
                onChange={(v) => setFilter(field, v)}
                options={[
                  { value: '', label: 'All' },
                  ...of(kind).map((o) => ({ value: o.code ?? '', label: o.label || '(blank)' })),
                ]}
              />
            </div>
          ))}
          {activeMore > 0 && (
            <button
              type="button"
              onClick={() =>
                setPageFilters((f) => ({ hideZeroQty: f.hideZeroQty }))
              }
              className="rounded-md border border-(--color-line-strong) bg-(--color-surface) px-3 py-2 text-(length:--text-small) font-semibold text-(--color-ink-2) hover:text-(--color-overdue)"
            >
              Clear these
            </button>
          )}
        </div>
      )}

      {/* ---- the totals of the WHOLE selection ----
           COLLAPSIBLE, like every other stats strip on the platform, and for
           the same measured reason: at 1280x720 this band is 73px above a grid
           that had 115px — a header and two rows. The figures summarise the
           lines underneath them, so when there is only room for one, the lines
           are the one worth showing. A remembered choice still wins forever;
           the screen only gets a say on a first visit under 820px of height. */}
      <CollapsibleStats storageKey="stats:sales-lines" label="Selection totals">
      {/* NO border-b AND NO VERTICAL PADDING of its own. The strip now sits
          inside CollapsibleStats, which already supplies a bar above it and an
          8px gap below; keeping the band's own separator and 20px of padding
          on top of that was 21px of chrome drawn twice. */}
      <div className="flex shrink-0 flex-wrap items-stretch gap-2 px-5 pb-1">
        <Figure measureKey="totalRows" label="Lines" value={totalRows.toLocaleString('en-US')} accent />
        <Figure measureKey="qty" label={`Quantity${lineUom.suffix}`} title={lineUom.hint} value={tot(totals?.qty, 'qty', (n) => n.toLocaleString('en-US', { maximumFractionDigits: 0 }))} />
        <Figure measureKey="totalSales" label="Sales" value={tot(totals?.sales, 'sales', (n) => compact(n, currency, lineCode))} title={totTitle(totals?.sales, 'sales', (n) => money(n, currency, lineCode))} />
        <Figure measureKey="totalCost" label="Cost" value={tot(totals?.cost, 'cost', (n) => compact(n, currency, lineCode))} title={totTitle(totals?.cost, 'cost', (n) => money(n, currency, lineCode), 'Requires the sales-cost permission')} />
        <Figure measureKey="totalCharges" label="Charges" value={tot(totals?.charges, 'charges', (n) => compact(n, currency, lineCode))} title={totTitle(totals?.charges, 'charges', (n) => money(n, currency, lineCode))} />
        <Figure measureKey="totalGp" label="Gross margin" value={tot(totals?.gp, 'gp', (n) => compact(n, currency, lineCode))} title={totTitle(totals?.gp, 'gp', (n) => money(n, currency, lineCode))} />
        <Figure measureKey="totalGpPct" label="Gross margin %" value={tot(totals?.gpPct, 'gpPct', (n) => `${(n * 100).toFixed(2)}%`)} />
        <p className="ml-auto self-center text-(length:--text-small) text-(--color-ink-3)">
          {/* Said WHERE the figures are, not only in the card subtitle: the
              totals cover the whole selection while the code beside them was
              read off this page, and a reader is owed both halves together. */}
          {lineCode ? `Amounts shown in ${lineCode} — this page's lines all agree. ` : ''}
          Totals are of the whole selection, not this page.
        </p>
      </div>
      </CollapsibleStats>

      {(v2 ? blankEverywhere.length > 0 : costMasked) && (
        <p className="shrink-0 border-b border-(--color-line) bg-(--color-surface-2) px-5 py-2 text-(length:--text-small) text-(--color-ink-3)">
          {v2
            ? `${blankEverywhere.join(', ')} ${blankEverywhere.length === 1 ? 'is' : 'are'} withheld from you on these lines.`
            : 'Cost, charges and margin require the sales-cost permission, so those columns show a dash.'}
        </p>
      )}

      {loading && <Loading label="Reading the ledger…" />}

      {!loading && rows.length === 0 ? (
        <EmptyState
          title="No lines in this view"
          detail="An empty result for the data you are permitted to see — not a refusal. Try widening the period or clearing a filter."
        />
      ) : (
        <>
          <div className="scroll-slim min-h-0 flex-1 overflow-auto">
            {/* border-SEPARATE so a pinned column cannot paint out the row
                lines, min-w-max so the table narrows when columns are put
                away, and the sticky header moves onto each cell so a pinned
                header wins the stacking contest against its own column. */}
            <table className="report-table w-full min-w-max border-separate border-spacing-0 text-(length:--text-body)">
              <thead>
                <tr>
                  {shown.map((c) => {
                    const sorted = c.sort && sortBy === c.sort
                    const pin = layout.pinOf(c.key)
                    const w = layout.widthOf(c.key)
                    // The body's own test: a figure column's width is a floor.
                    const figure = !!c.kind && c.kind !== 'text' && c.kind !== 'date'
                    return (
                      <th
                        key={c.key}
                        ref={layout.headerRef(c.key)}
                        aria-sort={sorted ? (sortDesc ? 'descending' : 'ascending') : undefined}
                        draggable
                        onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; setDragKey(c.key) }}
                        onDragOver={(e) => { if (!dragKey || dragKey === c.key) return; e.preventDefault(); setOverKey(c.key) }}
                        onDrop={(e) => {
                          e.preventDefault()
                          if (dragKey && dragKey !== c.key) layout.move(dragKey, layout.indexOf(c.key))
                          setDragKey(null); setOverKey(null)
                        }}
                        onDragEnd={() => { setDragKey(null); setOverKey(null) }}
                        style={{
                          width: w, minWidth: w, maxWidth: figure ? undefined : w,
                          left: pin === 'left' ? layout.offsetOf(c.key) : undefined,
                          right: pin === 'right' ? layout.offsetOf(c.key) : undefined,
                        }}
                        className={`relative sticky top-0 whitespace-nowrap border-b border-(--color-line-strong) px-3 py-2 ${
                          'text-center'
                        } ${pin ? 'z-30' : 'z-10'} ${overKey === c.key ? 'bg-(--color-navy-100)' : ''} ${
                          dragKey === c.key ? 'opacity-50' : ''
                        }`}
                      >
                        {/* A SORTABLE HEADER KEEPS ITS CLICK. Sorting is what a
                            reader came to it for, and a column that stopped
                            sorting the day somebody documented it would be a
                            strange bug to have to explain. So the explanation
                            rides beside it as a mark rather than taking the
                            click -- and a button inside a button is invalid
                            HTML anyway. Plain headers wrap normally. */}
                        <span data-head-label className="inline-block whitespace-nowrap">
                        {c.sort ? (
                          <>
                            <button
                              type="button"
                              onClick={() => toggleSort(c)}
                              className="eyebrow inline-flex items-center gap-1 hover:text-(--color-navy-700)"
                              title={`Sort by ${c.header}`}
                            >
                              {c.header}
                              {sorted && <span aria-hidden>{sortDesc ? '▾' : '▴'}</span>}
                            </button>
                            <ExplainMark surface="lines" measureKey={c.key} label={c.header} />
                          </>
                        ) : (
                          <Explain surface="lines" measureKey={c.key} className="eyebrow">
                            {c.header}
                          </Explain>
                        )}
                        </span>
                        <ResizeHandle
                          label={c.header}
                          widthOf={() => layout.headerWidth(c.key)}
                          minWidth={() => layout.minWidthOf(c.key)}
                          onResize={(px) => layout.resize(c.key, px)}
                          onAutoFit={() => layout.autoFit(c.key)}
                        />
                      </th>
                    )
                  })}
                </tr>
              </thead>
              <tbody>
                {rows.map((l, i) => {
                  const isReturn = l.docType === 'Return'
                  return (
                    <tr
                      key={`${l.docNumber}:${l.itemNumber}:${l.batch}:${i}`}
                      className="group border-(--color-line) hover:bg-(--color-surface-2)"
                    >
                      {shown.map((c) => {
                        const numeric = c.kind && c.kind !== 'text' && c.kind !== 'date'
                        const masked = c.cost && c.value(l) === null
                        const pin = layout.pinOf(c.key)
                        const w = layout.widthOf(c.key)
                        const raw = c.value(l)
                        return (
                          <td
                            key={c.key}
                            /* A figure is never cut (4 Oct 2026): no cap and no
                               ellipsis on a numeric cell, so its width is a
                               floor. Text still truncates, title on hover. */
                            style={{
                              width: w, maxWidth: numeric ? undefined : w,
                              left: pin === 'left' ? layout.offsetOf(c.key) : undefined,
                              right: pin === 'right' ? layout.offsetOf(c.key) : undefined,
                            }}
                            className={`${numeric ? '' : 'truncate'} whitespace-nowrap border-b border-(--color-line) px-3 py-1.5 ${numeric ? 'num text-right' : ''} ${
                              masked ? 'text-(--color-ink-3)'
                              : c.key === 'docType' && isReturn ? 'font-semibold text-(--color-overdue)'
                              : numeric && isReturn ? 'text-(--color-overdue)'
                              : 'text-(--color-ink)'
                            } ${pin ? 'sticky z-[1] bg-(--color-surface) group-hover:bg-(--color-surface-2)' : ''}`}
                            /* The full value on hover once a column can be
                               narrowed. This used to read `c.kind === 'text'`,
                               which no column ever sets — so every title was
                               undefined and nothing was recoverable. */
                            title={!numeric && raw !== null && raw !== '' ? String(raw) : undefined}
                          >
                            {cell(l, c)}
                          </td>
                        )
                      })}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* ---- paging ---- */}
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-(--color-line) px-5 py-2.5">
            <span className="text-(length:--text-small) text-(--color-ink-3)">
              Showing{' '}
              <span className="num font-semibold text-(--color-ink)">
                {((page - 1) * pageSize + 1).toLocaleString('en-US')}–
                {Math.min(page * pageSize, totalRows).toLocaleString('en-US')}
              </span>{' '}
              of <span className="num font-semibold text-(--color-ink)">{totalRows.toLocaleString('en-US')}</span> lines
            </span>
            {/* The page buttons drop under the row-count picker on a phone,
                where the two together are wider than the card. */}
            <div className="flex items-center gap-3 @max-3xl:flex-wrap">
              <label className="flex items-center gap-2 text-(length:--text-small) text-(--color-ink-3)">
                Rows
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="rounded border border-(--color-line-strong) bg-(--color-surface) px-2 py-1 text-(length:--text-small) text-(--color-ink)"
                >
                  {PAGE_SIZES.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
              <div className="flex items-center gap-1">
                <PageButton onClick={() => setPage(1)} disabled={page <= 1} label="First" />
                <PageButton onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} label="Previous" />
                <span className="px-2 text-(length:--text-small) text-(--color-ink-2)">
                  Page <span className="num font-semibold text-(--color-ink)">{page}</span> of{' '}
                  <span className="num">{pages.toLocaleString('en-US')}</span>
                </span>
                <PageButton onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page >= pages} label="Next" />
                <PageButton onClick={() => setPage(pages)} disabled={page >= pages} label="Last" />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

/** One figure in the totals strip. Compact by design — this is a band, not a KPI head. */
function Figure({ label, value, title, accent = false, measureKey }: { label: string; value: string; title?: string; accent?: boolean; measureKey?: string }) {
  return (
    <div className="relative min-w-[104px] overflow-hidden rounded-md border border-(--color-line) bg-(--color-surface) px-3 py-1.5">
      {accent && <span aria-hidden className="absolute inset-x-0 top-0 h-[2px] bg-(--color-navy-700)" />}
      {/* THE TOTALS ARE WHERE THE COST MASK BITES, and where a reviewer is most
          likely to reason wrongly. Cost, Charges and Gross margin cover only
          the companies this reader may see cost for, while Sales beside them
          covers all of them -- so dividing one by the other by hand gives a
          margin that is not the margin. The panel says so on each of them. */}
      <div className="text-(length:--text-micro) text-(--color-ink-3)">
        {measureKey ? (
          <Explain surface="lines" measureKey={measureKey}>
            {label}
          </Explain>
        ) : (
          label
        )}
      </div>
      <div className="num text-(length:--text-base) font-semibold text-(--color-ink)" title={title}>
        {value}
      </div>
    </div>
  )
}

function PageButton({ onClick, disabled, label }: { onClick: () => void; disabled: boolean; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="rounded border border-(--color-line-strong) px-2 py-1 text-(length:--text-small) font-medium text-(--color-ink-2) hover:bg-(--color-surface-2) disabled:cursor-not-allowed disabled:opacity-40"
    >
      {label}
    </button>
  )
}

/** "25 Feb 2026" in UTC, so a line dates the same for every reader. */
function dayLabel(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
}

/** "2.5K" for a button label that must not wrap. */
function compactCount(n: number): string {
  return n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}K` : String(n)
}

